import { useQuery } from '@apollo/client';
import { OptionalBoolean, parseQueryString, updateQueryString, OptionalString } from '@sixfold/app-utils';
import { Badge, Button, Input, Select, Stack } from '@sixfold/common-ui';
import { notNil } from '@sixfold/typed-primitives';
import React from 'react';
import { useLocation, useNavigate } from 'react-router';

import { InfiniteScroll } from '../../components/infinitescroll';
import {
  TelematicsProvidersListQuery,
  TelematicsProvidersListQueryVariables,
  getNodes,
  loadMoreFromConnection,
  PAGINATION_PAGE_SIZE,
  TelematicsProvidersSortInput,
  TelematicsProvidersSortKey,
  SortDirection,
} from '../../lib/graphql';
import { Loader } from '../../lib/loader';
import { useDebounce } from '../../lib/util/debounce';
import {
  ProvidersList,
  Props as ProvidersListProps,
  useProvidersListSort,
  SortableColumn,
} from '../components/providers_list';
import { integrationTypeLabelMap } from '../entities';
import { telematicsProvidersListQuery } from '../graphql';

import styles from './styles.module.css';

class ProvidersListLoadingContainer extends Loader<
  TelematicsProvidersListQuery,
  { telematicsProviders: ProvidersListProps['telematicsProviders']; countMatchingFilter: number },
  object
> {}

export const TelematicsProvidersContainer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const activeFilters = parseQueryString({
    queryString: location.search,
    queryParametersToParse: {
      integration_type: OptionalString,
      is_tour_based_provider: OptionalBoolean,
      is_adding_integration_disabled: OptionalBoolean,
      is_internal_use_only: OptionalBoolean,
      is_external_configuration_needed: OptionalBoolean,
      is_vehicle_management_enabled: OptionalBoolean,
      is_global_allocation_forbidden: OptionalBoolean,
      is_deprecated: OptionalBoolean,
      search: OptionalString,
    },
  });

  const clearFilters = () => void navigate(location.pathname);

  const { sort } = useProvidersListSort();

  const setActiveFilters = (name: keyof typeof activeFilters, value: string | boolean | undefined) => {
    void navigate(
      `${location.pathname}?${updateQueryString(location.search, {
        param: name,
        value,
      })}`,
    );
  };

  const debouncedSearch = useDebounce(activeFilters.search, 500);

  const accessibilityOptions = [
    { label: 'Internally accessible only', value: 'true' },
    { label: 'Publicly accessible', value: 'false' },
  ];

  const externalConfigOptions = [
    { label: 'External configuration needed', value: 'true' },
    { label: 'External configuration not needed', value: 'false' },
  ];

  const vehicleManagementOptions = [
    { label: 'Vehicle management enabled', value: 'true' },
    { label: 'Vehicle management not enabled', value: 'false' },
  ];

  const globalAllocationOptions = [
    { label: 'Global allocation forbidden', value: 'true' },
    { label: 'Global allocation allowed', value: 'false' },
  ];

  const addingIntegrationOptions = [
    { label: 'Adding integration disabled', value: 'true' },
    { label: 'Adding integration enabled', value: 'false' },
  ];

  const tourBasedProviderOptions = [
    { label: 'Is tour based provider', value: 'true' },
    { label: 'Is not tour based provider', value: 'false' },
  ];

  const integrationTypeOptions = Object.entries(integrationTypeLabelMap).map(([value, label]) => ({
    label,
    value,
  }));

  const deprecatedOptions = [
    { label: 'Is deprecated', value: 'true' },
    { label: 'Is not deprecated', value: 'false' },
  ];

  const result = useQuery<TelematicsProvidersListQuery, TelematicsProvidersListQueryVariables>(
    telematicsProvidersListQuery,
    {
      variables: {
        limit: PAGINATION_PAGE_SIZE,
        isInternalUseOnly: activeFilters.is_internal_use_only,
        isAddingIntegrationDisabled: activeFilters.is_adding_integration_disabled,
        isExternalConfigurationNeeded: activeFilters.is_external_configuration_needed,
        isVehicleManagementEnabled: activeFilters.is_vehicle_management_enabled,
        isGlobalAllocationForbidden: activeFilters.is_global_allocation_forbidden,
        isDeprecated: activeFilters.is_deprecated,
        isTourBasedProvider: activeFilters.is_tour_based_provider,
        integrationType: activeFilters.integration_type,
        textSearch: debouncedSearch ?? undefined,
        sort: sortToGraphql(sort),
      },
    },
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 180px)' }}>
      <Stack direction="row" alignItems="center" gap="small" wrap={true}>
        <div className={styles.searchInput}>
          <Input
            autoFocus
            size="small"
            name="providers-search"
            label="Providers search"
            labelPosition="hidden"
            placeholder="Providers search..."
            value={activeFilters.search ?? ''}
            onChange={({ value }) => {
              void navigate(
                `${location.pathname}?${updateQueryString(location.search, {
                  param: 'search',
                  value,
                })}`,
              );
            }}
          />
        </div>
        <Select
          label="Accessibility"
          labelPosition="hidden"
          placeholder="Accessibility"
          isClearable
          options={accessibilityOptions}
          value={accessibilityOptions.find((o) => o.value === activeFilters.is_internal_use_only?.toString()) ?? null}
          onChange={(option) => {
            setActiveFilters('is_internal_use_only', option ? option.value === 'true' : undefined);
          }}
          className={styles.compactFilterSelect}
        />
        <Select
          label="External configuration"
          labelPosition="hidden"
          placeholder="External configuration"
          isClearable
          options={externalConfigOptions}
          value={
            externalConfigOptions.find((o) => o.value === activeFilters.is_external_configuration_needed?.toString()) ??
            null
          }
          onChange={(option) => {
            setActiveFilters('is_external_configuration_needed', option ? option.value === 'true' : undefined);
          }}
          className={styles.compactFilterSelect}
        />
        <Select
          label="Vehicle management"
          labelPosition="hidden"
          placeholder="Vehicle management"
          isClearable
          options={vehicleManagementOptions}
          value={
            vehicleManagementOptions.find((o) => o.value === activeFilters.is_vehicle_management_enabled?.toString()) ??
            null
          }
          onChange={(option) => {
            setActiveFilters('is_vehicle_management_enabled', option ? option.value === 'true' : undefined);
          }}
          className={styles.compactFilterSelect}
        />
        <Select
          label="Global allocation"
          labelPosition="hidden"
          placeholder="Global allocation"
          isClearable
          options={globalAllocationOptions}
          value={
            globalAllocationOptions.find((o) => o.value === activeFilters.is_global_allocation_forbidden?.toString()) ??
            null
          }
          onChange={(option) => {
            setActiveFilters('is_global_allocation_forbidden', option ? option.value === 'true' : undefined);
          }}
          className={styles.compactFilterSelect}
        />
        <Select
          label="Adding integration"
          labelPosition="hidden"
          placeholder="Adding integration"
          isClearable
          options={addingIntegrationOptions}
          value={
            addingIntegrationOptions.find(
              (o) => o.value === activeFilters.is_adding_integration_disabled?.toString(),
            ) ?? null
          }
          onChange={(option) => {
            setActiveFilters('is_adding_integration_disabled', option ? option.value === 'true' : undefined);
          }}
          className={styles.compactFilterSelect}
        />
        <Select
          label="Tour based provider"
          labelPosition="hidden"
          placeholder="Tour based provider"
          isClearable
          options={tourBasedProviderOptions}
          value={
            tourBasedProviderOptions.find((o) => o.value === activeFilters.is_tour_based_provider?.toString()) ?? null
          }
          onChange={(option) => {
            setActiveFilters('is_tour_based_provider', option ? option.value === 'true' : undefined);
          }}
          className={styles.compactFilterSelect}
        />
        <Select
          label="Integration type"
          labelPosition="hidden"
          placeholder="Integration type"
          isClearable
          options={integrationTypeOptions}
          value={integrationTypeOptions.find((o) => o.value === activeFilters.integration_type?.toString()) ?? null}
          onChange={(option) => {
            setActiveFilters('integration_type', option ? option.value : undefined);
          }}
          className={styles.compactFilterSelect}
        />
        <Select
          label="Deprecated"
          labelPosition="hidden"
          placeholder="Deprecated"
          isClearable
          options={deprecatedOptions}
          value={deprecatedOptions.find((o) => o.value === activeFilters.is_deprecated?.toString()) ?? null}
          onChange={(option) => {
            setActiveFilters('is_deprecated', option ? option.value === 'true' : undefined);
          }}
          className={styles.compactFilterSelect}
        />
        {Object.values(activeFilters).some(notNil) && (
          <Button onClick={clearFilters} size="xsmall">
            Clear filters
          </Button>
        )}
      </Stack>
      <ProvidersListLoadingContainer
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        result={result as any}
        mapData={({ data }) => ({
          telematicsProviders: getNodes(data.telematicsProvidersV2?.providers).map((provider) => ({
            id: provider.telematicsProviderId,
            name: provider.telematicsProviderName,
            integrationType: provider.integrationType,
            isTourBasedProvider: provider.isTourBasedProvider,
            isAddingIntegrationDisabled: provider.isAddingIntegrationDisabled,
            isInternalUseOnly: provider.isInternalUseOnly,
            integrationInstructionsURL: provider.integrationInstructionsURL,
            isExternalConfigurationNeeded: provider.isExternalConfigurationNeeded,
            isVehicleManagementEnabled: provider.isVehicleManagementEnabled,
            isGlobalAllocationForbidden: provider.isGlobalAllocationForbidden,
            isDeprecated: provider.isDeprecated,
            numIntegrations: provider.telematicsIntegrations.count,
            numActiveIntegrations: provider.enabledIntegrations.count,
            numTotalTrucks: provider.totalTrucksCount,
            numActiveTrucks: provider.activeTrucksCount,
            recentAllocationsCount: provider.recentAllocationsCount,
          })),
          countMatchingFilter: data.telematicsProvidersV2?.count ?? 0,
        })}>
        {({ telematicsProviders, countMatchingFilter }) => (
          <Stack direction="column" gap="medium" style={{ flex: 1, minHeight: 0 }}>
            <Badge size="medium" kind="neutral">
              {telematicsProviders.length < countMatchingFilter
                ? `Showing ${telematicsProviders.length} out of ${countMatchingFilter} providers`
                : `${countMatchingFilter} providers`}
            </Badge>
            <InfiniteScroll
              className="providers-list"
              triggerOnWindow={false}
              loadMoreEntries={() =>
                loadMoreFromConnection(telematicsProvidersListQuery, result, ['telematicsProvidersV2', 'providers'])
              }>
              <ProvidersList telematicsProviders={telematicsProviders} />
            </InfiniteScroll>
          </Stack>
        )}
      </ProvidersListLoadingContainer>
    </div>
  );
};

export const sortToGraphql = (sort: ReturnType<typeof useProvidersListSort>['sort']): TelematicsProvidersSortInput => {
  return {
    key: sortToGraphqlMap[sort.key],
    direction: sort.direction === 'ASC' ? SortDirection.ASCENDING : SortDirection.DESCENDING,
  };
};

const sortToGraphqlMap = {
  name: TelematicsProvidersSortKey.PROVIDER_NAME,
  numIntegrations: TelematicsProvidersSortKey.INTEGRATIONS_COUNT,
  numActiveIntegrations: TelematicsProvidersSortKey.ACTIVE_INTEGRATIONS_COUNT,
  numTotalTrucks: TelematicsProvidersSortKey.TRUCKS_COUNT,
  numActiveTrucks: TelematicsProvidersSortKey.ACTIVE_TRUCKS_COUNT,
  recentAllocationsCount: TelematicsProvidersSortKey.RECENT_ALLOCATIONS_COUNT,
} satisfies {
  [K in SortableColumn]: TelematicsProvidersSortInput['key'];
};
